import { useMemo, useRef, useState } from 'react';
import { Dices, GripVertical, Plus, Shuffle, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { createRng, randomSeed } from '../../core/rng';
import {
  CURATED_PALETTES,
  HARMONY_LABELS,
  paletteColors,
  resolvePalette,
  type HarmonyMode,
  type PaletteSpec,
} from '../../core/palette';
import { hslToRgb, toHex, type ColorSpace } from '../../core/color';
import { Segmented, Select } from './Basics';
import { Slider } from './Slider';

/**
 * Editor for a `palette` parameter.
 *
 * Three sources behind one preview strip, so switching between a curated set, a
 * hand-built stop list and an algorithmically derived palette is a single click and
 * the result is always visible as both discrete stops and the continuous ramp
 * techniques actually sample.
 */
export function PaletteEditor({
  value,
  onChange,
}: {
  value: PaletteSpec;
  onChange: (spec: PaletteSpec) => void;
}): JSX.Element {
  const [seed, setSeed] = useState(1);
  const dragIndex = useRef<number | null>(null);

  // Generative palettes depend on the random stream; re-rolling gives a new draw of
  // the same recipe, which is the point of the dice button.
  const stops = useMemo(() => paletteColors(value, createRng(seed)), [value, seed]);
  const resolved = useMemo(() => resolvePalette(value, createRng(seed)), [value, seed]);

  const rampCss = useMemo(() => {
    const parts: string[] = [];
    for (let i = 0; i <= 16; i++) parts.push(`${resolved.at(i / 16)} ${(i / 16) * 100}%`);
    return `linear-gradient(90deg, ${parts.join(', ')})`;
  }, [resolved]);

  const patch = (p: Partial<PaletteSpec>): void => onChange({ ...value, ...p });

  const setColor = (index: number, color: string): void => {
    const colors = [...stops];
    colors[index] = color;
    patch({ source: 'custom', colors });
  };

  return (
    <div className="space-y-2 py-1">
      {/* Continuous ramp — what techniques sample. */}
      <div className="h-5 w-full rounded border border-ink-700" style={{ background: rampCss }} />

      {/* Discrete stops. */}
      <div className="flex flex-wrap gap-1">
        {stops.map((color, i) => (
          <div
            key={`${color}-${i}`}
            draggable={value.source === 'custom'}
            onDragStart={() => (dragIndex.current = i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragIndex.current;
              dragIndex.current = null;
              if (from === null || from === i) return;
              const colors = [...stops];
              const [moved] = colors.splice(from, 1);
              colors.splice(i, 0, moved);
              patch({ source: 'custom', colors });
            }}
            className="group/stop relative"
          >
            <label
              className="relative block h-7 w-7 cursor-pointer overflow-hidden rounded border border-ink-700"
              title={color}
            >
              <span className="absolute inset-0" style={{ background: color }} />
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(i, e.target.value)}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
            </label>
            {value.source === 'custom' && (
              <>
                <GripVertical
                  size={9}
                  className="pointer-events-none absolute -left-0.5 top-0 text-white/50 opacity-0 group-hover/stop:opacity-100"
                />
                {stops.length > 1 && (
                  <button
                    type="button"
                    title="Farbstopp entfernen"
                    onClick={() => patch({ source: 'custom', colors: stops.filter((_, j) => j !== i) })}
                    className="absolute -right-1 -top-1 hidden h-3.5 w-3.5 items-center justify-center rounded-full
                               bg-ink-900 text-ink-300 hover:text-red-400 group-hover/stop:flex"
                  >
                    <Trash2 size={8} />
                  </button>
                )}
              </>
            )}
          </div>
        ))}

        <button
          type="button"
          title="Farbstopp hinzufügen"
          onClick={() => patch({ source: 'custom', colors: [...stops, stops[stops.length - 1] ?? '#ffffff'] })}
          className="flex h-7 w-7 items-center justify-center rounded border border-dashed border-ink-600 text-ink-500 transition-colors hover:border-ink-500 hover:text-ink-300"
        >
          <Plus size={12} />
        </button>
      </div>

      <Segmented
        size="sm"
        value={value.source}
        onChange={(source) => patch({ source })}
        options={[
          { value: 'curated', label: 'Kuratiert' },
          { value: 'custom', label: 'Eigene' },
          { value: 'generative', label: 'Generativ' },
        ]}
      />

      {value.source === 'curated' && (
        <div className="grid grid-cols-2 gap-1">
          {CURATED_PALETTES.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => patch({ paletteId: p.id })}
              className={clsx(
                'flex items-center gap-1.5 rounded px-1.5 py-1 text-left transition-colors',
                value.paletteId === p.id ? 'bg-ink-700' : 'bg-ink-850 hover:bg-ink-800',
              )}
            >
              <span className="flex h-3 w-8 shrink-0 overflow-hidden rounded-sm">
                {p.colors.map((c) => (
                  <span key={c} className="h-full flex-1" style={{ background: c }} />
                ))}
              </span>
              <span className="truncate text-[10px] text-ink-300">{p.name}</span>
            </button>
          ))}
        </div>
      )}

      {value.source === 'generative' && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <label className="relative h-6 w-8 shrink-0 cursor-pointer overflow-hidden rounded border border-ink-700">
              <span className="absolute inset-0" style={{ background: value.base }} />
              <input
                type="color"
                value={value.base}
                onChange={(e) => patch({ base: e.target.value })}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
            </label>
            <div className="min-w-0 flex-1">
              <Select
                value={value.harmony}
                onChange={(harmony: HarmonyMode) => patch({ harmony })}
                options={(Object.keys(HARMONY_LABELS) as HarmonyMode[]).map((h) => ({
                  value: h,
                  label: HARMONY_LABELS[h],
                }))}
              />
            </div>
            <button
              type="button"
              title="Basisfarbe neu würfeln"
              onClick={() => {
                const rng = createRng(randomSeed());
                patch({ base: toHex(hslToRgb({ h: rng.next(), s: 0.45 + rng.next() * 0.4, l: 0.42 + rng.next() * 0.25 })) });
                setSeed(randomSeed());
              }}
              className="ghost-button h-6 w-6 shrink-0 p-0"
            >
              <Dices size={12} />
            </button>
          </div>
          <Slider label="Stopps" value={value.count} onChange={(count) => patch({ count: Math.round(count) })} min={2} max={12} step={1} defaultValue={5} precision={0} />
          <Slider label="Sättigungsstreuung" value={value.satSpread} onChange={(satSpread) => patch({ satSpread })} min={0} max={0.6} step={0.01} defaultValue={0.15} />
          <Slider label="Helligkeitsstreuung" value={value.lightSpread} onChange={(lightSpread) => patch({ lightSpread })} min={0} max={0.5} step={0.01} defaultValue={0.22} />
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-ink-500">Mischraum</span>
        <div className="min-w-0 flex-1">
          <Segmented
            size="sm"
            value={value.space}
            onChange={(space: ColorSpace) => patch({ space })}
            options={[
              { value: 'oklch', label: 'OKLCH', title: 'Wahrnehmungsgleich — empfohlen' },
              { value: 'hsl', label: 'HSL' },
              { value: 'rgb', label: 'RGB' },
            ]}
          />
        </div>
        <button
          type="button"
          title="Reihenfolge umkehren"
          onClick={() => patch({ reverse: !value.reverse })}
          className={clsx('ghost-button h-6 w-6 shrink-0 p-0', value.reverse && 'text-accent-500')}
        >
          <Shuffle size={11} />
        </button>
      </div>
    </div>
  );
}
